import { format, isWithinInterval, setHours, setMinutes, formatISO, addDays, subDays, isBefore, isAfter, isEqual } from "date-fns";
import { Authorization, CompTime } from "src/types/__generated__/typescript-operations";

const daysOfWeek = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];

const groupAvailabilitiesByDayAndTime = (availabilityTimes: { startTime: string, endTime: string, day: string; caregiverName?: string }[]) => {
  const availabilityTimesGroupedByDay = availabilityTimes.reduce((acc: {
    [key: string]: { startTime: string, endTime: string, caregiverName?: string }[]
  }, { startTime, endTime, day, caregiverName }) => {
    const timeSlot = { startTime, endTime, caregiverName };
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(timeSlot);
    return acc;
  }, {});

  const grouped = Object.entries(availabilityTimesGroupedByDay).reduce((acc: {
    [key: string]: {
      days: string[],
      timeSlots: { startTime: string, endTime: string, caregiverName?: string }[],
      key: string
    }
  }, [day, slots]) => {
    const key = JSON.stringify(slots);

    if (!acc[key]) {
      acc[key] = { days: [], timeSlots: slots, key };
    }

    acc[key].days.push(day);
    return acc;
  }, {});

  return Object.values(grouped);
}

const generateAvailabilityEvents = ({ availability, selectedDays, type, viewType, acctContactRelation }:
{ availability, selectedDays: Date[], type: string, viewType: string, acctContactRelation?: any }) => {
  if (!availability.startDate || !availability.times) return [];

  const selectedStart = new Date(selectedDays[0]);
  const selectedEnd = new Date(selectedDays[selectedDays.length - 1]);
  console.log('selectedDays', selectedDays)

  const startDate = new Date(Math.max(new Date(availability.startDate || selectedStart).getTime(), selectedStart.getTime()));
  const endDate = new Date(Math.min(new Date(availability.endDate || selectedEnd).getTime(), selectedEnd.getTime()));
  if (startDate > endDate) return [];
  return selectedDays
    .filter((currentDate) => isWithinInterval(new Date(currentDate), { start: startDate, end: endDate }))
    .flatMap((currentDate) => {
      const dayName = daysOfWeek[currentDate.getDay()];
      return availability.times
        .filter(({ day }) => day === dayName)
        .map(({ startTime, endTime, sfCaregiver }) => {
          const [startHour, startMinute] = startTime.split(':').map(Number);
          const [endHour, endMinute] = endTime.split(':').map(Number);
          const start = setMinutes(setHours(new Date(currentDate), startHour), startMinute);
          const end = setMinutes(setHours(new Date(currentDate), endHour), endMinute);
          return {
            start: format(start, "yyyy-MM-dd'T'HH:mm:ss"),
            end: format(end, "yyyy-MM-dd'T'HH:mm:ss"),
            classNames: [type],
            extendedProps: {
              viewType,
              acctContactRelation,
              [type]: {
                id: availability.id,
                sfCaregiver,
                patient: availability.patient,
              },
            },
            title: `${formatHours(start)} - ${formatHours(end)}`,
          };
        });
    });
}

const generateCompTimeEvents = ({ compTimes, slotMinTime }: {
  compTimes: CompTime[],
  slotMinTime: string
}) => compTimes.map((compTime) => {
  const date = new Date(compTime.date);
  date.setHours(parseInt(slotMinTime.split(':')[0], 10), 0, 0, 0);
  return {
    allDay: true,
    start: date.toISOString(),
    end: date.toISOString(),
    extendedProps: { compTime },
    classNames: ['compTime']
  };
});

const formatTime = (time: string) => {
  // Remove 'Z' suffix if present to treat time as local, not UTC
  const localTime = time.replace('Z', '');
  return format(new Date(`1970-01-01T${localTime}`), 'h:mm a');
}

const minTime = (times) => times.reduce((a, b) => (a < b ? a : b), Infinity);
const maxTime = (times) => times.reduce((a, b) => (a > b ? a : b), -Infinity);

const getEventHours = (event) => {
  const startDate = new Date(event.start);
  const endDate = new Date(event.end);
  const startHour = startDate.getHours();
  const endHour = endDate.getHours();

  const isOvernight = endHour < startHour;

  return {
    startHour: isOvernight ? 0 : startHour,
    endHour: isOvernight ? 24 : endHour
  };
};

const getCalendarSlotTimes = (events) => {
  const defaultStartHour = 9;
  const defaultEndHour = 16;

  const validEvents = events.filter((e) => e.start !== e.end);
  const hours = validEvents.map(getEventHours);
  const earliestStart = validEvents.length
    ? minTime(hours.map((h) => h.startHour))
    : defaultStartHour;

  const latestEnd = validEvents.length
    ? maxTime(hours.map((h) => h.endHour))
    : defaultEndHour;

  const formatTime = (hour) => `${hour.toString().padStart(2, '0')}:00:00`;

  return {
    slotMinTime: earliestStart < defaultStartHour ? formatTime(earliestStart) : formatTime(defaultStartHour),
    slotMaxTime: latestEnd > defaultEndHour
      ? formatTime(Math.min(latestEnd + 1, 24))
      : formatTime(defaultEndHour + 1)
  };
};

const categorizeItems = <T extends { startDate: string; endDate?: string; override?: boolean }>(items: T[]) => {
  const today = new Date();

  return items.reduce(
    (acc, item) => {
      const startDate = new Date(item.startDate);
      const endDate = item.endDate ? new Date(item.endDate) : null;

      if (startDate <= today && (!endDate || endDate >= today)) {
        acc.grouped.active.push(item);
        if (!item.override) {
          acc.current = item;
        }
      } else if (startDate > today) {
        acc.grouped.upcoming.push(item);
      } else if (endDate && endDate < today) {
        acc.grouped.past.push(item);
      }

      return acc;
    },
    {
      grouped: { active: [] as T[], upcoming: [] as T[], past: [] as T[] },
      current: undefined as T | undefined,
    }
  );
};

const formatHours = (date: Date) => format(date, date.getMinutes() === 0 ? 'ha' : 'h:mma').toLowerCase()

// Parse date or return null if it's invalid
const toDate = (date) => (date ? new Date(date) : null);

// Check if two availabilities overlap
const isOverlapping = (availability, override) => {
  const availabilityStart = toDate(availability.startDate);
  const availabilityEnd = toDate(availability.endDate);
  const overrideStart = toDate(override.startDate);
  const overrideEnd = toDate(override.endDate);

  // If availability is ongoing, treat it as ending far in the future (to allow any override to happen)
  const adjustedAvailabilityEnd = availabilityEnd || new Date(9999, 12, 31);

  return (
    (isBefore(availabilityStart, overrideEnd) || isEqual(availabilityStart, overrideEnd))
    && (isAfter(adjustedAvailabilityEnd, overrideStart) || isEqual(adjustedAvailabilityEnd, overrideStart))
  );
};

// Process availabilities and include overrides and modifications
const processAvailabilities = <T extends { startDate: string; endDate?: string; override?: boolean }>(availabilities: T[]) => {
  const processed = availabilities.map((current) => {
    if (current.override) {
      // If it's an override, include it directly in the result
      return current;
    }

    // Find any overlapping overrides
    const overlappingOverrides = availabilities.filter(
      (override) => override.override && isOverlapping(current, override)
    );

    // If no overlaps, return the current availability as it is
    if (overlappingOverrides.length === 0) {
      return current;
    }

    const result = overlappingOverrides.flatMap((override) => {
      const availabilityStart = toDate(current.startDate);
      const availabilityEnd = toDate(current.endDate) || new Date(9999, 12, 31);
      const overrideStart = toDate(override.startDate);
      const overrideEnd = toDate(override.endDate);

      // Case 1: If the availability starts before the override and ends after the override starts
      if (isBefore(availabilityStart, overrideStart) && isAfter(availabilityEnd, overrideStart)) {
        return [{
          ...current,
          endDate: formatISO(subDays(overrideStart, 1)), // End before the override starts
        }];
      }

      // Case 2: If the availability ends after the override and starts before the override ends
      if (isAfter(availabilityEnd, overrideEnd) && isBefore(availabilityStart, overrideEnd)) {
        return [{
          ...current,
          startDate: formatISO(addDays(overrideEnd, 1)), // Start after the override ends
        }];
      }

      // Case 3: If the availability SPANS the override (wraps around it)
      if (isBefore(availabilityStart, overrideStart) && isAfter(availabilityEnd, overrideEnd)) {
        return [
          {
            ...current,
            endDate: formatISO(subDays(overrideStart, 1)), // First part ends before the override starts
          },
          {
            ...current,
            startDate: formatISO(addDays(overrideEnd, 1)), // Second part starts after the override ends
          }
        ];
      }

      // Case 4: If the availability is WITHIN the override (completely covered) - FIXED CONDITION
      if (
        (isAfter(availabilityStart, overrideStart) || isEqual(availabilityStart, overrideStart)) &&
        (isBefore(availabilityEnd, overrideEnd) || isEqual(availabilityEnd, overrideEnd))
      ) {
        // Exclude this availability as it's fully overridden
        return [];
      }

      // If no overlap, just return the current availability unchanged
      return [current];
    });

    // Flatten the result in case it's an array of arrays
    return result.flat();
  });

  // Flatten the array to merge any split availabilities into one array
  return processed.flat();
};

const MINUTES_IN_HOUR = 60;

const formatMinToTime = (mins: number): string => {
  const hours = Math.floor(mins / MINUTES_IN_HOUR);
  const minutes = mins % MINUTES_IN_HOUR;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const createScheduleTime = (
  day: string,
  hours: number
) => {
  if (hours <= 0) return null;
  const START_HOUR = 9;
  const MAX_END_HOUR = 23;
  const MAX_END_MINUTE = 59;
  const totalMinutes = Math.round(hours * MINUTES_IN_HOUR);
  const defaultStart = START_HOUR * MINUTES_IN_HOUR;
  const defaultEnd = defaultStart + totalMinutes;
  const maxEnd = MAX_END_HOUR * MINUTES_IN_HOUR + MAX_END_MINUTE;

  const start = defaultEnd > maxEnd
    ? Math.max(0, maxEnd - totalMinutes)
    : defaultStart;

  const end = start + totalMinutes;

  return {
    day,
    startTime: formatMinToTime(start),
    endTime: formatMinToTime(end),
    caregiverId: undefined,
    sfCaregiverId: undefined,
    caregiverName: undefined
  };
};

const getScheduledDaysFromCount = (numDays: number): string[] => {
  // Start with Monday-Friday, then add Saturday, then Sunday if needed
  if (numDays <= 5) {
    return daysOfWeek.slice(1, 1 + numDays); // Monday through needed weekday
  }
  if (numDays === 6) {
    return daysOfWeek.slice(1, 7); // Monday through Saturday
  }
  // All 7 days, Monday-first: Monday through Sunday
  return [...daysOfWeek.slice(1), daysOfWeek[0]];
};

// Normalize time string to HH:mm:ss format, stripping 'Z' suffix and milliseconds
const normalizeTimeString = (time: string): string => {
  const match = time.match(/^(\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : time;
};

function getScheduleTimesFromAuthorization(
  auth: Authorization
) {
  // 1. HIGHEST PRIORITY: Specific day start/end times (sundayStartTime/sundayEndTime, etc.)
  const specificDayStartEndTimes = daysOfWeek.map((day) => {
    const startTimeField = `${day.toLowerCase()}StartTime` as keyof Authorization;
    const endTimeField = `${day.toLowerCase()}EndTime` as keyof Authorization;
    const startTime = auth[startTimeField];
    const endTime = auth[endTimeField];

    if (startTime && endTime && typeof startTime === 'string' && typeof endTime === 'string') {
      return {
        day,
        // Normalize times to strip 'Z' suffix so they're treated as local times
        startTime: normalizeTimeString(startTime),
        endTime: normalizeTimeString(endTime),
        caregiverId: undefined,
        sfCaregiverId: undefined,
        caregiverName: undefined
      };
    }
    return null;
  }).filter((t) => t !== null);

  if (specificDayStartEndTimes.length > 0) {
    return specificDayStartEndTimes;
  }

  // 2. Specific day hours (like sundayHours, mondayHours, etc.)
  const specificDayTimes = daysOfWeek.map((day) => {
    const fieldName = `${day.toLowerCase()}Hours` as keyof Authorization;
    const hours = auth[fieldName];
    if (hours && typeof hours === 'number' && hours > 0) {
      return createScheduleTime(day, hours);
    }
    return null;
  }).filter((t) => t !== null);

  if (specificDayTimes.length > 0) {
    return specificDayTimes;
  }

  // 3. Weekly options (daysPerWeek and hoursPerDay combinations)
  // 3a. If both hoursPerWeek and daysPerWeek (but no hoursPerDay)
  if (auth.hoursPerWeek && auth.daysPerWeek && !auth.hoursPerDay) {
    const hoursPerDay = auth.hoursPerWeek / auth.daysPerWeek;
    const scheduledDays = getScheduledDaysFromCount(auth.daysPerWeek);
    return scheduledDays
      .map((day) => createScheduleTime(day, hoursPerDay))
      .filter(Boolean);
  }

  // 3b. Only hoursPerWeek (spread across 5 weekdays)
  if (auth.hoursPerWeek && !auth.daysPerWeek) {
    const hoursPerDay = auth.hoursPerWeek / 5;
    const mondayToFriday = daysOfWeek.slice(1, 6);
    return mondayToFriday
      .map((day) => createScheduleTime(day, hoursPerDay))
      .filter(Boolean);
  }

  // 3c. daysPerWeek and hoursPerDay (plus optional second set)
  if (auth.daysPerWeek && auth.hoursPerDay) {
    const totalDays = auth.daysPerWeek + (auth.daysPerWeek2 || 0);
    if (totalDays > 7) {
      throw new Error('You cannot schedule more than 7 days per week');
    }

    // Use getScheduledDaysFromCount for consistent Monday-first ordering
    const weekDays = getScheduledDaysFromCount(totalDays);

    const firstDays = weekDays.slice(0, auth.daysPerWeek);
    const firstSet = firstDays.map((day) => createScheduleTime(day, auth.hoursPerDay!));

    const hoursForSecond = auth.hoursPerDay2 || auth.hoursPerDay;
    const secondSet = (auth.daysPerWeek2 && hoursForSecond)
      ? weekDays.slice(auth.daysPerWeek, totalDays).map((day) => createScheduleTime(day, hoursForSecond!))
      : [];

    return [...firstSet, ...secondSet].filter(Boolean);
  }

  // 4. Monthly hours (distributed across weekdays) - check before period to match estimatedWeekly priority
  if (auth.hoursPerMonth) {
    // Distribute monthly hours across weekdays (Monday-Friday)
    const weekdaysPerMonth = 21.67; // Average weekdays per month (5 days * 52 weeks / 12 months)
    const hoursPerDay = auth.hoursPerMonth / weekdaysPerMonth;
    const mondayToFriday = daysOfWeek.slice(1, 6); // Monday to Friday
    return mondayToFriday
      .map((day) => createScheduleTime(day, hoursPerDay))
      .filter(Boolean);
  }

  // 5. Period with days (hoursPerPeriod + periodDays)
  if (auth.hoursPerPeriod && auth.periodDays) {
    // Convert period hours to weekly hours first
    const weeksInPeriod = auth.periodDays / 7;
    const weeklyHours = auth.hoursPerPeriod / weeksInPeriod;
    const hoursPerDay = weeklyHours / 5; // Distribute across 5 weekdays
    const mondayToFriday = daysOfWeek.slice(1, 6); // Monday to Friday
    return mondayToFriday
      .map((day) => createScheduleTime(day, hoursPerDay))
      .filter(Boolean);
  }

  // 6. Period with months (hoursPerPeriod + periodMonths)
  if (auth.hoursPerPeriod && auth.periodMonths) {
    // Convert period hours to weekly hours first
    const WEEKS_PER_MONTH = 52 / 12; // 4.33 weeks per month
    const weeksInPeriod = auth.periodMonths * WEEKS_PER_MONTH;
    const weeklyHours = auth.hoursPerPeriod / weeksInPeriod;
    const hoursPerDay = weeklyHours / 5; // Distribute across 5 weekdays
    const mondayToFriday = daysOfWeek.slice(1, 6); // Monday to Friday
    return mondayToFriday
      .map((day) => createScheduleTime(day, hoursPerDay))
      .filter(Boolean);
  }

  // 7. Fallback: weeklyHoursPerDay
  if (auth.weeklyHoursPerDay) {
    const mondayToFriday = daysOfWeek.slice(1, 6);
    return mondayToFriday
      .map((day) => createScheduleTime(day, auth.weeklyHoursPerDay!))
      .filter(Boolean);
  }

  throw new Error('No schedule hours found on the authorization.');
}

export {
  groupAvailabilitiesByDayAndTime,
  formatTime,
  generateAvailabilityEvents,
  getCalendarSlotTimes,
  categorizeItems,
  formatHours,
  processAvailabilities,
  generateCompTimeEvents,
  getScheduleTimesFromAuthorization
}
